import { ref, computed } from '@vue/composition-api';
import { useAuth } from '@/modules/auth/api';
import { baseLayersDefinition } from '@/compositions/map/base-layer';
import { objectPropertyService, objectService } from '@/modules/common/api';
import { prepareObject } from '@/provider/utils';
import { inject, provide } from 'vue-demi';
import { getTheme } from '@/modules/account/useTheme';
import { markerSizes } from '@/compositions/map/marker-styles';

const defaultProgramSettings = [
  {
    key: 'programDefaultPosition',
    value: [-0.12766, 51.507351]
  },
  {
    key: 'programDefaultRotation',
    value: 0
  },
  {
    key: 'programDefaultScale',
    value: 4
  },
  {
    key: 'programSizeOfObjectCardOnMap',
    value: 'medium'
  },
  {
    key: 'programSizeOfObjectsOnMap',
    value: markerSizes.DEFAULT
  },
  {
    key: 'programMap',
    value: baseLayersDefinition[1].id // no map
  },
  {
    key: 'programAvailableMaps',
    value: { osm: true, google_satellite: true }
  },
  {
    key: 'programDefaultGeotag',
    value: ''
  },
  {
    key: 'programDefaultObjectGroup',
    value: ''
  },
  {
    key: 'programAllowAllGeotags',
    value: false
  },
  {
    key: 'programDisplayGeozonesByDefault',
    value: false
  },
  {
    key: 'programDisplayLandmarksByDefault',
    value: false
  },
  {
    key: 'programOsmUrl',
    value: ''
  },
  {
    key: 'programShowMonitoringItems',
    value: false
  },
  {
    key: 'programEntityFilters',
    value: {}
  }
];

const themes = [
  { id: 1, text: 'Classic', value: false },
  { id: 2, text: 'Dark', value: true }
];

const languages = [
  { id: 1, text: 'English', value: 'en' },
  { id: 2, text: 'Chinese', value: 'cn' },
  { id: 3, text: 'Chinese (Simplified)', value: 'cn_simplified' },
  { id: 4, text: 'Thai (auto)', value: 'th' },
  { id: 5, text: 'Indonesian (auto)', value: 'id' },
  { id: 6, text: 'Filipino (auto)', value: 'fl' },
  { id: 7, text: 'Hindi (auto)', value: 'hi' },
  { id: 8, text: 'Malay (auto)', value: 'ms' },
  { id: 9, text: 'Russian', value: 'ru' }
];

const sizes = [
  { id: 1, text: 'Small', value: markerSizes.POINT },
  { id: 2, text: 'Medium', value: markerSizes.MINI },
  { id: 3, text: 'Large', value: markerSizes.DEFAULT }
];

function createProgramSettingsStore() {
  const programSettings = ref(null);
  const centerPosition = ref(['', '']);

  const { getUserProfileId } = useAuth();

  const theme = ref(getTheme());

  const language = ref(languages[0]);

  const maps = computed(() => {
    const availableMaps =
      programSettings.value?.programAvailableMaps?.value || {};
    return baseLayersDefinition.filter(
      map => !!availableMaps[map.id] || map.id === 'empty'
    );
  });

  const load = async () => {
    const id = await getUserProfileId();

    programSettings.value = prepareObject(
      await objectService.fetch(id, { fetchPolicy: 'network-only' })
    );
    const position = programSettings.value['programDefaultPosition'].value;

    programSettings.value['programDefaultPosition'].value = position.split(',');

    defaultProgramSettings.forEach(({ key, value }) => {
      // if we have program setting but value is not defined than use def value
      if (
        programSettings.value[key]?.id &&
        (programSettings.value[key]?.value === undefined ||
          programSettings.value[key]?.value === null)
      ) {
        programSettings.value[key].value = value;
      }
    });
  };

  const update = async properties => {
    await objectPropertyService.updateValues(properties);
    await load();
  };

  const saveEntityFilter = async (storageKey, filter) => {
    const newFilterValue = {
      ...(programSettings.value['programEntityFilters'].value || {}),
      [storageKey]: filter
    }
    programSettings.value['programEntityFilters'].value = newFilterValue;

    objectPropertyService.updateValue(
      programSettings.value['programEntityFilters'].id,
      newFilterValue
    );
  };

  const getEntityFilter = key => {
    return programSettings.value['programEntityFilters']?.value?.[key];
  };

  return {
    theme,
    themes,
    language,
    close,
    languages,
    sizes,
    centerPosition,
    programSettings,
    update,
    maps,
    load,
    saveEntityFilter,
    getEntityFilter
  };
}

export const ProgramSettingsProviderSymbol = Symbol(
  'Program settings identifier'
);

export const useProgramSettingsProvider = () => {
  provide(ProgramSettingsProviderSymbol, createProgramSettingsStore());
};

export function useProgramSettings() {
  return inject(ProgramSettingsProviderSymbol);
}
